import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  BarChart3, CalendarRange, Cloud, CloudOff, Command, Compass, Filter, Map as MapIcon,
  Moon, Mountain, Play, Route as RouteIcon, Settings2, Sun,
} from 'lucide-react'
import AdminPanel from './components/AdminPanel'
import CommandPalette from './components/CommandPalette'
import StatsDashboard from './components/StatsDashboard'
import CountryDrawer from './components/CountryDrawer'
import PhotoDrawer from './components/PhotoDrawer'
import PlaceDrawer from './components/PlaceDrawer'
import SummitMap from './components/SummitMap'
import { useSummitData } from './hooks/useSummitData'
import { galleryForPhoto, photosForRoutePoint, photosForSummit } from './lib/photos'
import SeasonMatrix from './components/SeasonMatrix'

const THEME_ORDER = ['dark', 'progress', 'light']
const THEME_LABELS = {
  dark: 'Dark',
  progress: 'Progress',
  light: 'Light',
}

function nextTheme(theme) {
  const index = THEME_ORDER.indexOf(theme)
  return THEME_ORDER[(index + 1 + THEME_ORDER.length) % THEME_ORDER.length]
}

function ThemeIcon({ theme }) {
  if (theme === 'progress') return <MapIcon size={16} />
  if (theme === 'light') return <Sun size={16} />
  return <Moon size={16} />
}

function formatTripDates(trip) {
  if (!trip.start_date) return 'Dates open'
  const formatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' })
  const start = formatter.format(new Date(`${trip.start_date}T12:00:00`))
  if (!trip.end_date) return start
  const end = formatter.format(new Date(`${trip.end_date}T12:00:00`))
  return `${start} — ${end}`
}

export default function App() {
  const {
    trips,
    summits,
    routePoints,
    routeLegs,
    routeLegsReady,
    routeLegsError,
    photos,
    photosReady,
    photosError,
    loading,
    error,
    source,
    cloudEmpty,
    session,
    refresh,
  } = useSummitData()

  const [selectedTripId, setSelectedTripId] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedSummit, setSelectedSummit] = useState(null)
  const [selectedRoutePoint, setSelectedRoutePoint] = useState(null)
  const [selectedPhoto, setSelectedPhoto] = useState(null)
  const [adminOpen, setAdminOpen] = useState(false)
  const [statsOpen, setStatsOpen] = useState(false)
  const [seasonOpen, setSeasonOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const mapApiRef = useRef(null)
  const [theme, setTheme] = useState(() => {
    const saved = window.localStorage.getItem('summit-hub-theme')
    if (THEME_ORDER.includes(saved)) return saved
    return 'light'
  })

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    window.localStorage.setItem('summit-hub-theme', theme)
  }, [theme])

  const handleSelectSummit = useCallback((summit) => {
    setSelectedRoutePoint(null)
    setSelectedPhoto(null)
    setSelectedSummit(summit)
  }, [])
  const handleSelectRoutePoint = useCallback((point) => {
    setSelectedSummit(null)
    setSelectedPhoto(null)
    setSelectedRoutePoint(point)
  }, [])
  const handleSelectPhoto = useCallback((photo) => {
    setSelectedSummit(null)
    setSelectedRoutePoint(null)
    setSelectedPhoto(photo)
  }, [])

  const handleMapApi = useCallback((api) => { mapApiRef.current = api }, [])

  useEffect(() => {
    function onKeyDown(event) {
      const key = String(event.key || '').toLowerCase()
      if ((event.metaKey || event.ctrlKey) && key === 'k') {
        event.preventDefault()
        setPaletteOpen((current) => !current)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const paletteItems = useMemo(() => {
    const summitItems = summits.map((summit) => ({
      id: `summit-${summit.id}`,
      group: 'Summits',
      label: `${summit.flag ? `${summit.flag} ` : ''}${summit.peak_name}`,
      sublabel: `${summit.country_name} · ${Number(summit.elevation_m || 0).toLocaleString()} m · ${summit.status || 'future'}`,
      keywords: `${summit.country_name} ${summit.country_code || ''} ${summit.status || ''}`,
      icon: <Mountain size={15} />,
      run: () => {
        if (summit.trip_id) setSelectedTripId(summit.trip_id)
        handleSelectSummit(summit)
        mapApiRef.current?.flyTo(summit.longitude, summit.latitude, 8.2)
      },
    }))

    const tripItems = trips.map((trip) => ({
      id: `trip-${trip.id}`,
      group: 'Trips',
      label: trip.name,
      sublabel: `${trip.status} · ${summits.filter((summit) => summit.trip_id === trip.id).length} summits`,
      keywords: trip.summary || '',
      icon: <RouteIcon size={15} />,
      run: () => setSelectedTripId(trip.id),
    }))

    const viewItems = [
      {
        id: 'cmd-all-routes',
        group: 'Views',
        label: 'Show all routes',
        sublabel: 'Clear the trip filter',
        icon: <MapIcon size={15} />,
        run: () => { setSelectedTripId('all'); setStatusFilter('all') },
      },
      {
        id: 'cmd-tour',
        group: 'Views',
        label: 'Fly the current route',
        sublabel: 'Cinematic pass through every stop',
        icon: <Play size={15} />,
        run: () => mapApiRef.current?.startTour(),
      },
      {
        id: 'cmd-stats',
        group: 'Views',
        label: 'Open statistics',
        sublabel: 'Progress, elevation and leave efficiency',
        icon: <BarChart3 size={15} />,
        run: () => setStatsOpen(true),
      },
      {
        id: 'cmd-manage',
        group: 'Views',
        label: 'Open manage panel',
        sublabel: 'Summits, trips, routes, photos, planning',
        icon: <Settings2 size={15} />,
        run: () => setAdminOpen(true),
      },
      {
        id: 'cmd-theme',
        group: 'Views',
        label: `Switch map style to ${THEME_LABELS[nextTheme(theme)]}`,
        sublabel: `Currently ${THEME_LABELS[theme]}`,
        icon: <MapIcon size={15} />,
        run: () => setTheme((current) => nextTheme(current)),
      },
      ...['all', 'completed', 'planned', 'future'].map((status) => ({
        id: `cmd-filter-${status}`,
        group: 'Views',
        label: `Filter: ${status} summits`,
        sublabel: 'Status filter',
        icon: <Filter size={15} />,
        run: () => setStatusFilter(status),
      })),
    ]

    return [...summitItems, ...tripItems, ...viewItems]
  }, [summits, trips, theme, handleSelectSummit, handleMapApi])

  const selectedTrip = useMemo(
    () => trips.find((trip) => trip.id === selectedTripId) || null,
    [trips, selectedTripId],
  )

  const selectedSummitTrip = useMemo(
    () => trips.find((trip) => trip.id === selectedSummit?.trip_id) || null,
    [trips, selectedSummit],
  )
  const selectedRouteTrip = useMemo(
    () => trips.find((trip) => trip.id === selectedRoutePoint?.trip_id) || null,
    [trips, selectedRoutePoint],
  )
  const selectedPhotoTrip = useMemo(
    () => trips.find((trip) => trip.id === selectedPhoto?.trip_id) || null,
    [trips, selectedPhoto],
  )
  const selectedSummitPhotos = useMemo(() => photosForSummit(photos, selectedSummit), [photos, selectedSummit])
  const selectedRoutePhotos = useMemo(() => photosForRoutePoint(photos, selectedRoutePoint), [photos, selectedRoutePoint])
  const selectedPhotoGallery = useMemo(() => galleryForPhoto(photos, selectedPhoto), [photos, selectedPhoto])

  const activeTrip = trips.find((trip) => trip.status === 'active') || trips[0]

  const efficiency = selectedTrip?.leave_days
    ? (Number(selectedTrip.countries_count || 0) / Number(selectedTrip.leave_days)).toFixed(2)
    : null

  return (
    <main className="app-shell">
      <header className="top-nav">
        <button className="brand" onClick={() => {
          setSelectedTripId('all')
          setStatusFilter('all')
        }}>
	<button className="stats-button" onClick={() => setSeasonOpen(true)}>
            <CalendarRange size={16} /> <span>Season</span>
          </button>
          <span className="brand-mark"><Mountain size={20} strokeWidth={2.5} /></span>
          <span><strong>Summit Hub</strong><small>195 countries · one defining summit</small></span>
        </button>

        <div className="top-nav__actions">
          <button
            className="theme-toggle"
            onClick={() => setTheme((current) => nextTheme(current))}
            aria-label={`Map style: ${THEME_LABELS[theme]}. Switch to ${THEME_LABELS[nextTheme(theme)]}.`}
            title={`Map style: ${THEME_LABELS[theme]} · click for ${THEME_LABELS[nextTheme(theme)]}`}
          >
            <ThemeIcon theme={theme} />
            <span>{THEME_LABELS[theme]}</span>
          </button>
          <div className={`sync-pill sync-pill--${source}`} title={error || undefined}>
            {source === 'cloud' ? <Cloud size={14} /> : <CloudOff size={14} />}
            <span>{source === 'cloud' ? 'Live sync' : source === 'demo-connected' ? 'Cloud ready' : 'Demo mode'}</span>
          </div>
          <button className="palette-button" onClick={() => setPaletteOpen(true)} title="Search summits and views (Ctrl/⌘ K)">
            <Command size={14} /> <span>Search</span> <kbd>K</kbd>
          </button>
          <button className="stats-button" onClick={() => setStatsOpen(true)}>
            <BarChart3 size={16} /> <span>Stats</span>
          </button>
          <button className="manage-button" onClick={() => setAdminOpen(true)}>
            <Settings2 size={16} /> Manage
          </button>
        </div>
      </header>

      <section className="map-stage">
        <SummitMap
          key={theme}
          trips={trips}
          summits={summits}
          routePoints={routePoints}
          routeLegs={routeLegs}
          photos={photos}
          theme={theme}
          selectedTripId={selectedTripId}
          statusFilter={statusFilter}
          onSelectSummit={handleSelectSummit}
          onSelectRoutePoint={handleSelectRoutePoint}
          onSelectPhoto={handleSelectPhoto}
          onMapApi={handleMapApi}
        />



        <div className="status-filter glass-panel" aria-label="Filter summit status">
          {['all', 'completed', 'planned', 'future'].map((status) => (
            <button
              key={status}
              className={statusFilter === status ? 'active' : ''}
              onClick={() => setStatusFilter(status)}
            >
              {status}
            </button>
          ))}
        </div>

        <aside className="trip-rail glass-panel">
          <div className="trip-rail__head">
            <div>
              <div className="eyebrow">Journey map</div>
              <strong>{selectedTrip ? selectedTrip.name : 'All routes'}</strong>
            </div>
            {efficiency && <span title="Countries per annual-leave day">{efficiency} / AL</span>}
          </div>

          <button className={`trip-row ${selectedTripId === 'all' ? 'active' : ''}`} onClick={() => setSelectedTripId('all')}>
            <i style={{ background: '#d8ff63' }} />
            <span><strong>All routes</strong><small>{summits.length} summit targets</small></span>
          </button>

          <div className="trip-rail__scroll">
            {trips.map((trip) => {
              const count = summits.filter((summit) => summit.trip_id === trip.id).length
              return (
                <button key={trip.id} className={`trip-row ${selectedTripId === trip.id ? 'active' : ''}`} onClick={() => setSelectedTripId(trip.id)}>
                  <i style={{ background: trip.color }} />
                  <span><strong>{trip.name}</strong><small>{formatTripDates(trip)} · {count} summit{count === 1 ? '' : 's'}</small></span>
                  <em>{trip.status}</em>
                </button>
              )
            })}
          </div>
        </aside>
	
	        {(selectedTripId !== 'all' || statusFilter !== 'all') && (
          <button className="reset-view" onClick={() => {
            setSelectedTripId('all')
            setStatusFilter('all')
          }}>
            <MapIcon size={13} /> All routes
          </button>
        )}

        {activeTrip && (
          <div className="next-expedition glass-panel">
            <div className="next-expedition__icon"><Compass size={19} /></div>
            <div>
              <div className="eyebrow">Current expedition</div>
              <strong>{activeTrip.name}</strong>
              <span><CalendarRange size={13} /> {formatTripDates(activeTrip)}{activeTrip.leave_days !== null && activeTrip.leave_days !== undefined ? ` · ${activeTrip.leave_days} leave days` : ''}</span>
            </div>
            <button onClick={() => setSelectedTripId(activeTrip.id)}>Explore</button>
          </div>
        )}

        {loading && <div className="loading-chip">Loading cloud routes…</div>}
        {error && <div className="error-chip">Cloud unavailable — showing demo data</div>}
      </section>

      <CountryDrawer summit={selectedSummit} trip={selectedSummitTrip} photos={selectedSummitPhotos} onClose={() => setSelectedSummit(null)} />
      <PlaceDrawer point={selectedRoutePoint} trip={selectedRouteTrip} photos={selectedRoutePhotos} onClose={() => setSelectedRoutePoint(null)} />
      <PhotoDrawer photos={selectedPhotoGallery} selectedPhoto={selectedPhoto} trip={selectedPhotoTrip} onClose={() => setSelectedPhoto(null)} />

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={paletteItems} />

      <StatsDashboard
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        trips={trips}
        summits={summits}
        routeLegs={routeLegs}
        photos={photos}
      />

	<SeasonMatrix
        open={seasonOpen}
        onClose={() => setSeasonOpen(false)}
        summits={summits}
        session={session}
        refresh={refresh}
      />

      <AdminPanel
        open={adminOpen}
        onClose={() => setAdminOpen(false)}
        trips={trips}
        summits={summits}
        routePoints={routePoints}
        routeLegs={routeLegs}
        routeLegsReady={routeLegsReady}
        routeLegsError={routeLegsError}
        photos={photos}
        photosReady={photosReady}
        photosError={photosError}
        session={session}
        cloudEmpty={cloudEmpty}
        refresh={refresh}
      />
    </main>
  )
}
